import type { CSS } from '@stitches/react';
import React, { useCallback } from 'react';

import { Icon } from '@/components/shared/element/icons';
import CameraIcon from '@/components/shared/element/icons/global/camera';
import Camera360Icon from '@/components/shared/element/icons/global/camera-360';
import EngineIcon from '@/components/shared/element/icons/global/engine';
import FinalGearIcon from '@/components/shared/element/icons/global/final-gear';
import GearIcon from '@/components/shared/element/icons/global/gear';
import LeafIcon from '@/components/shared/element/icons/global/leaf';
import PlayArrowIcon from '@/components/shared/element/icons/global/play-arrow';
import RearIcon from '@/components/shared/element/icons/global/rear';
import SeatIcon from '@/components/shared/element/icons/global/seat';
import TorqueIcon from '@/components/shared/element/icons/global/torque';
import WeightIcon from '@/components/shared/element/icons/global/weight';
import WheelIcon from '@/components/shared/element/icons/global/wheel';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { normaliseToArray } from '@/components/shared/utility/format';
import { ICVIcon } from '@/contents/cv/cv-compare';
import { ILCVIcon } from '@/contents/lcv/lcv-compare';
import { CvModelClassWithCvModel } from '@/services/contentstack/models/cv-model-class';
import { styled } from '@/stitches.config';
import { ITest } from '@/types/tracking';

export interface ContentItemWithIconProps {
  css?: CSS;
  title: string;
  value: string | string[];
  icon?: ILCVIcon | ICVIcon;
  model: CvModelClassWithCvModel;
  hasTextFromIcon?: boolean;
  test?: ITest;
}

interface TextTorqueProps {
  text: string;
  splitText?: string;
}

const SummaryItemContainer = styled(Box, {
  textAlign: 'center',
});

const SplitText = ({ text, splitText = 'ที่' }: TextTorqueProps) => {
  const parts = text.split(splitText);
  return (
    <>
      {parts.length > 1 ? (
        <>
          <Text font="bold" size="text-body-2" variant="text-gray-dark">
            {parts[0]} ที่
          </Text>
          <Text font="bold" size="text-body-2" variant="text-gray-dark">
            {parts[1]}
          </Text>
        </>
      ) : (
        <Text font="bold" size="text-body-2" variant="text-gray-dark">
          {text}
        </Text>
      )}
    </>
  );
};

const MultipleValue = ({
  items,
}: {
  items: Pick<CvModelClassWithCvModel, 'models'>;
}) => {
  return items.models.map((model, index) => (
    <Flex
      key={index}
      direction={{ '@initial': 'column', '@lg': 'row' }}
      alignItems="center"
      justifyContent="center"
      columnGap="2"
    >
      <Text font="bold" size="text-body-2" variant="text-gray-dark">
        รุ่น {model.name}:
      </Text>
      <Text font="bold" variant="text-gray-dark">
        {model.ce}
      </Text>
    </Flex>
  ));
};

export const ContentItemWithIcon: React.FC<ContentItemWithIconProps> = ({
  css,
  model,
  title,
  value,
  icon,
  hasTextFromIcon,
  test,
}: ContentItemWithIconProps) => {
  const details = normaliseToArray(value);
  const formatValue = (val: string) => {
    if (!val || val === 'ไม่มี') return '-';
    return val;
  };

  const getIconName = useCallback((icon: string) => {
    const iconLookup = {
      engine: <EngineIcon />,
      torque: <TorqueIcon />,
      gear: <GearIcon />,
      wheel: <WheelIcon />,
      seat: <SeatIcon />,
      'play-arrow': <PlayArrowIcon />,
      camera: <CameraIcon />,
      'camera-360': <Camera360Icon />,
      final_gear_ratio: <FinalGearIcon />,
      payload: <WeightIcon />,
      ce: <RearIcon />,
      leaf: <LeafIcon />,
    };
    return iconLookup[icon] || '';
  }, []);

  const getTextFromIconType = useCallback(
    (icon: string) => {
      switch (icon) {
        case 'gear':
          return model?.summary?.gear ?? '-';
        case 'final_gear_ratio':
          return model?.summary?.final_gear_ratio ?? '-';
        case 'payload':
          return model?.summary?.payload ?? '-';
        case 'leaf':
          return model?.summary?.treatment ?? '-';
        default:
          return '';
      }
    },
    [model]
  );

  return (
    <>
      <SummaryItemContainer css={css} test={test ? test : undefined}>
        <Icon size="lg" css={{ marginBottom: '$space-2' }}>
          {getIconName(icon as string)}
        </Icon>
        <Text size="text-caption" variant="text-gray-darkest">
          {title}
        </Text>

        {details.length === 0 || !details ? (
          <>
            {icon === 'engine' && hasTextFromIcon ? (
              <>
                {!!model?.summary?.ps && (
                  <Text font="bold" size="text-body-2" variant="text-gray-dark">
                    {model?.summary?.ps}
                  </Text>
                )}
                <Text font="bold" size="text-body-2" variant="text-gray-dark">
                  {model?.summary?.engine_name ?? '-'}
                </Text>
              </>
            ) : (
              <Text
                className="empty-text"
                size="text-body-2"
                font="bold"
                variant="text-gray-dark"
              >
                -
              </Text>
            )}
          </>
        ) : (
          <>
            {!!hasTextFromIcon && (
              <Text
                className="text-from-icon"
                size="text-body-2"
                font="bold"
                variant="text-gray-dark"
                dangerouslySetInnerHTML={{
                  __html: getTextFromIconType(icon as string),
                }}
              />
            )}

            {details?.map((detail, index) => {
              const desc = formatValue(detail);
              return (
                <React.Fragment key={index}>
                  {icon === 'torque' ? (
                    <SplitText text={desc} />
                  ) : (
                    !hasTextFromIcon && (
                      <Text
                        size="text-body-2"
                        font="bold"
                        variant="text-gray-dark"
                        dangerouslySetInnerHTML={{
                          __html: desc,
                        }}
                      />
                    )
                  )}

                  {icon === 'ce' &&
                    (model?.summary?.show_model_ce &&
                    model?.models.length > 0 ? (
                      <MultipleValue items={model} />
                    ) : (
                      <Text
                        font="bold"
                        size="text-body-2"
                        variant="text-gray-dark"
                      >
                        {model.summary.ce}
                      </Text>
                    ))}
                </React.Fragment>
              );
            })}
          </>
        )}
      </SummaryItemContainer>
    </>
  );
};

ContentItemWithIcon.displayName = 'ContentItemWithIcon';
